'use client'

const QUEUE_DB_NAME = 'surveyanalytica-offline-queue'
const QUEUE_STORE_NAME = 'pending-requests'

/**
 * Opens or creates the offline queue database
 */
function openQueueDB() {
  return new Promise((resolve, reject) => {
    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this environment'))
      return
    }

    const request = indexedDB.open(QUEUE_DB_NAME, 1)
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(QUEUE_STORE_NAME)) {
        const store = db.createObjectStore(QUEUE_STORE_NAME, { 
          keyPath: 'id', 
          autoIncrement: true 
        })
        store.createIndex('timestamp', 'timestamp', { unique: false })
        store.createIndex('url', 'url', { unique: false })
      }
    }
    
    request.onsuccess = () => resolve(request.result)
    request.onerror = (event) => reject(event.target.error)
  })
}

/**
 * Adds a request to the offline queue
 * @param {string} url - The request URL
 * @param {string} method - HTTP method
 * @param {any} body - Request body
 * @param {object} headers - Request headers
 */
export async function queueRequest(url, method, body, headers = {}) {
  try {
    const db = await openQueueDB()
    const tx = db.transaction(QUEUE_STORE_NAME, 'readwrite')
    const store = tx.objectStore(QUEUE_STORE_NAME)
    
    const request = {
      url,
      method,
      body,
      headers,
      timestamp: Date.now()
    }
    
    return new Promise((resolve, reject) => {
      const addRequest = store.add(request)
      addRequest.onsuccess = () => resolve(addRequest.result)
      addRequest.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error('Error queueing request:', error)
    throw error
  }
}

/**
 * Gets all pending requests from the queue
 */
export async function getPendingRequests() {
  try {
    const db = await openQueueDB()
    const tx = db.transaction(QUEUE_STORE_NAME, 'readonly')
    const store = tx.objectStore(QUEUE_STORE_NAME)
    
    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error('Error getting pending requests:', error)
    return []
  }
}

/**
 * Removes a request from the queue
 * @param {number} id - The request ID
 */
export async function removeRequest(id) {
  try {
    const db = await openQueueDB()
    const tx = db.transaction(QUEUE_STORE_NAME, 'readwrite')
    const store = tx.objectStore(QUEUE_STORE_NAME)
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error('Error removing request:', error)
    throw error
  }
}

/**
 * Clears all pending requests from the queue
 */
export async function clearQueue() {
  try {
    const db = await openQueueDB()
    const tx = db.transaction(QUEUE_STORE_NAME, 'readwrite')
    const store = tx.objectStore(QUEUE_STORE_NAME)
    
    return new Promise((resolve, reject) => {
      const request = store.clear()
      request.onsuccess = () => resolve()
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error('Error clearing queue:', error)
    throw error
  }
}

/**
 * Gets the count of pending requests
 */
export async function getQueueCount() {
  try {
    const db = await openQueueDB()
    const tx = db.transaction(QUEUE_STORE_NAME, 'readonly')
    const store = tx.objectStore(QUEUE_STORE_NAME)
    
    return new Promise((resolve, reject) => {
      const request = store.count()
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error('Error getting queue count:', error)
    return 0
  }
}
